import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/react'
import { useAuthStore } from '../hooks/useAuthStore'

/* ══════════════════════════════════════════════════════════
   STITCH DESIGN TOKENS
══════════════════════════════════════════════════════════ */
const C = {
  primary:             '#4800b2',
  primaryContainer:    '#6200ee',
  primaryFixed:        '#e8ddff',
  primaryFixedDim:     '#cfbdff',
  secondary:           '#5e5d6d',
  secondaryFixed:      '#e3e0f4',
  tertiary:            '#6f2300',
  tertiaryFixed:       '#ffdbcf',
  background:          '#f8f9fd',
  surfaceLowest:       '#ffffff',
  surfaceContainerLow: '#f2f3f7',
  surfaceContainer:    '#edeef2',
  surfaceContainerHigh:'#e7e8ec',
  outline:             '#7a7488',
  outlineVariant:      '#cbc3d9',
  onSurface:           '#191c1f',
  onSurfaceVariant:    '#494456',
}

/* ── Global CSS overrides (injected once into <head>) ── */
const globalCSS = `
/* --- Stitch Dashboard Scoped Overrides --- */

/* Force Material Symbols font for icon spans */
.material-symbols-outlined {
  font-family: 'Material Symbols Outlined' !important;
  font-style: normal !important;
  font-weight: normal !important;
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  line-height: 1 !important;
  letter-spacing: normal !important;
  text-transform: none !important;
  display: inline-block !important;
  white-space: nowrap !important;
  word-wrap: normal !important;
  direction: ltr !important;
  -webkit-font-smoothing: antialiased !important;
}
.msym-fill { font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24 !important; }

/* Hero banner — override any Geex h2/p colour */
.dash-hero-title {
  color: #ffffff !important;
  font-family: 'Manrope', sans-serif !important;
  font-size: 1.875rem !important;
  font-weight: 800 !important;
  line-height: 1.2 !important;
  margin: 0 0 10px !important;
  letter-spacing: -0.02em !important;
}
.dash-hero-sub {
  color: #cfbdff !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 1rem !important;
  font-weight: 300 !important;
  opacity: 0.9 !important;
}
.dash-hero-badge span { color: rgba(255,255,255,0.9) !important; }

/* Stat card hover */
.dash-stat-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.09) !important; }

/* Tool card hover */
.dash-tool-card:hover {
  border-color: rgba(72,0,178,0.3) !important;
  box-shadow: 0 4px 16px rgba(72,0,178,0.08) !important;
}
.dash-tool-card:hover .dash-tool-icon { transform: scale(1.10) !important; }
.dash-tool-icon { transition: transform 0.18s ease; }

/* Nav row hover */
.dash-nav-row:hover { background: #f2f3f7 !important; }

/* FAB */
.dash-fab { transition: all 0.18s ease !important; }
.dash-fab:hover { transform: scale(1.10) !important; background: #6200ee !important; }
.dash-fab:hover .dash-fab-icon { transform: rotate(90deg) !important; }
.dash-fab-icon { transition: transform 0.25s ease; display: inline-block !important; }

/* Buttons */
.dash-btn-primary:hover { filter: brightness(1.12); }
.dash-btn-outline:hover { background: rgba(72,0,178,0.05) !important; }

/* Pulsing dot */
@keyframes dashPulseGreen { 0%,100%{opacity:1} 50%{opacity:0.35} }
.dash-pulse { animation: dashPulseGreen 1.4s ease-in-out infinite; }

/* Section headings — override Geex */
.dash-section-title {
  font-family: 'Manrope', sans-serif !important;
  font-size: 1.125rem !important;
  font-weight: 800 !important;
  color: #191c1f !important;
  margin: 0 !important;
}
`

/* ── Icon component (uses .material-symbols-outlined directly) ── */
function Icon({ name, size = 20, color, fill = false, className = '', style: extraStyle = {} }) {
  return (
    <span
      className={`material-symbols-outlined${fill ? ' msym-fill' : ''}${className ? ' ' + className : ''}`}
      style={{ fontSize: size, color, lineHeight: 1, verticalAlign: 'middle', ...extraStyle }}
    >{name}</span>
  )
}

/* ── Tool Card ── */
function ToolCard({ icon, iconColor, iconBg, label, sub, path }) {
  return (
    <Link to={path} style={{ textDecoration: 'none' }}>
      <div className="dash-tool-card" style={{
        background: '#fff', padding: '20px 12px', borderRadius: 16,
        border: `1px solid ${C.surfaceContainerHigh}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        textAlign: 'center', cursor: 'pointer',
        transition: 'border-color 0.18s, box-shadow 0.18s',
      }}>
        <div className="dash-tool-icon" style={{
          width: 48, height: 48, background: iconBg, borderRadius: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 12px',
        }}>
          <Icon name={icon} size={24} color={iconColor} />
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.onSurface, marginBottom: 3, fontFamily: 'Manrope, sans-serif', lineHeight: 1.3 }}>{label}</div>
        <div style={{ fontSize: 9, color: C.outline, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{sub}</div>
      </div>
    </Link>
  )
}

/* ── Activity Row ── */
function ActivityRow({ icon, iconColor, iconBg, title, sub, time }) {
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <div style={{
        width: 40, height: 40, borderRadius: '50%', background: iconBg, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name={icon} size={18} color={iconColor} />
      </div>
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.onSurface }}>{title}</div>
        <div style={{ fontSize: 11, color: C.outline, marginTop: 2 }}>{sub}</div>
        <div style={{ fontSize: 9, color: C.outline, opacity: 0.5, marginTop: 4, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>{time}</div>
      </div>
    </div>
  )
}

/* ── Nav Item ── */
function NavItem({ icon, iconColor, iconBg, label, sub, path }) {
  return (
    <Link to={path} style={{ textDecoration: 'none' }}>
      <div className="dash-nav-row" style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: '10px 12px',
        borderRadius: 16, cursor: 'pointer', transition: 'background 0.15s',
      }}>
        <div style={{
          width: 40, height: 40, background: iconBg, borderRadius: 12, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name={icon} size={20} color={iconColor} />
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.onSurface }}>{label}</div>
          <div style={{ fontSize: 10, color: C.outline, marginTop: 1 }}>{sub}</div>
        </div>
      </div>
    </Link>
  )
}

/* ── White Card ── */
function Card({ children, style = {} }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 24,
      border: `1px solid ${C.surfaceContainerHigh}`,
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      ...style,
    }}>{children}</div>
  )
}

/* ══════════════════════════════════════════════════════════
   DASHBOARD PAGE
══════════════════════════════════════════════════════════ */
export default function Dashboard() {
  const { user: clerkUser } = useUser()
  const { user: storeUser }  = useAuthStore()
  const navigate = useNavigate()

  const displayName = clerkUser?.fullName || clerkUser?.firstName || storeUser?.name || 'Doctor'
  const hour        = new Date().getHours()
  const greeting    = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening'

  const [stats, setStats] = useState({ consultations: 0, letters: 0, users: 0, activeUsers: 0 })
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) return
    fetch('/api/admin/stats.php', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        if (d.success) setStats({
          consultations: d.stats?.total_consultations || 0,
          letters:       d.stats?.total_letters       || 0,
          users:         d.stats?.total_users         || 0,
          activeUsers:   d.stats?.active_users        || 0,
        })
      }).catch(() => {})
  }, [])

  const tools = [
    { icon: 'coronavirus',         iconColor: '#f87171', iconBg: '#fef2f2', label: 'ID Disease AI',    sub: 'Infectious Disease', path: '/my-consult' },
    { icon: 'medical_information', iconColor: '#4ade80', iconBg: '#f0fdf4', label: 'General Medicine', sub: 'GPT-4 Powered',      path: '/my-consult-openai' },
    { icon: 'mic',                 iconColor: '#c084fc', iconBg: '#faf5ff', label: 'Voice Agent',      sub: 'Voice Consultation', path: '/my-voice-agent' },
    { icon: 'psychiatry',          iconColor: '#60a5fa', iconBg: '#eff6ff', label: 'Ask Dr Sam',       sub: 'AI Doctor Chat',     path: '/ask-dr-sam' },
    { icon: 'description',         iconColor: '#fb923c', iconBg: '#fff7ed', label: 'Medical Letters',  sub: 'AI Correspondence',  path: '/letters' },
    { icon: 'video_call',          iconColor: '#818cf8', iconBg: '#eef2ff', label: 'Video Consult',    sub: 'Real-time Video',    path: '/video-chat' },
    { icon: 'quiz',                iconColor: '#2dd4bf', iconBg: '#f0fdfa', label: 'MCQ Test',         sub: 'Knowledge Assessment', path: '/mcq-test' },
    { icon: 'calendar_month',      iconColor: '#e879f9', iconBg: '#fdf4ff', label: 'Book Appointment', sub: 'Schedule Patients',  path: '/book-appointment' },
  ]

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', color: C.onSurface, background: C.background, paddingBottom: 80 }}>
      <style dangerouslySetInnerHTML={{ __html: globalCSS }} />

      {/* ── 1. HEADER ACTIONS ROW ── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 28, flexWrap: 'wrap', gap: 16,
      }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, color: C.outline, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>{greeting}</p>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: C.onSurface, margin: 0, fontFamily: 'Manrope, sans-serif', lineHeight: 1 }}>{displayName}</h2>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button className="dash-btn-primary"
            onClick={() => navigate('/my-consult')}
            style={{
              background: C.primary, color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px 22px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(72,0,178,0.25)',
              fontFamily: 'Manrope, sans-serif', transition: 'filter 0.15s',
            }}>New Consultation</button>
          <button className="dash-btn-outline"
            onClick={() => navigate('/book-appointment')}
            style={{
              background: '#fff', color: C.primary,
              border: `1.5px solid ${C.primary}`, borderRadius: 8,
              padding: '10px 22px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'Manrope, sans-serif', transition: 'background 0.15s',
            }}>Book Appointment</button>
        </div>
      </div>

      {/* ── 2. HERO BANNER ── */}
      <section style={{ marginBottom: 36 }}>
        <div style={{
          background: 'linear-gradient(to right, #4800b2, #6200ee)',
          borderRadius: 16, padding: '32px 36px',
          position: 'relative', overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(72,0,178,0.22)',
        }}>
          {/* Decorative circles */}
          <div style={{ position: 'absolute', right: '-5%',  top: '-20%',    width: 256, height: 256, background: 'rgba(255,255,255,0.05)', borderRadius: '50%', filter: 'blur(48px)', zIndex: 0 }} />
          <div style={{ position: 'absolute', right: '10%',  bottom: '-30%', width: 192, height: 192, background: 'rgba(255,255,255,0.10)', borderRadius: '50%', filter: 'blur(32px)', zIndex: 0 }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Badge */}
            <div className="dash-hero-badge" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(255,255,255,0.10)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.20)',
              borderRadius: 9999, padding: '4px 14px', marginBottom: 16,
            }}>
              <span className="dash-pulse" style={{ width: 8, height: 8, borderRadius: '50%', background: '#4ade80', display: 'inline-block', flexShrink: 0 }} />
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.9)' }}>AI Platform Active</span>
            </div>

            {/* Title — className carries !important CSS override */}
            <h2 className="dash-hero-title">Your AI-Powered Telehealth Platform</h2>

            {/* Subtitle */}
            <p className="dash-hero-sub" style={{ maxWidth: 600, margin: 0 }}>
              Instant consultations, smart medical letters, HD video calls — all in one place.
            </p>
          </div>
        </div>
      </section>

      {/* ── 3. STATS GRID ── */}
      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 20, marginBottom: 32 }}>

        {/* Total Consultations */}
        <div className="dash-stat-card" style={{ background: '#fff', borderRadius: 16, padding: 24, border: `1px solid ${C.surfaceContainerHigh}`, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'box-shadow 0.2s' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div style={{ width: 40, height: 40, background: C.primaryFixed, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="stethoscope" size={20} color={C.primary} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#22c55e', display: 'flex', alignItems: 'center', gap: 2 }}>
              12% <Icon name="trending_up" size={14} color="#22c55e" />
            </span>
          </div>
          <p style={{ fontSize: 28, fontWeight: 900, color: C.onSurface, margin: '0 0 4px', fontFamily: 'Manrope, sans-serif' }}>{stats.consultations}</p>
          <p style={{ fontSize: 12, fontWeight: 600, color: C.outline, margin: 0 }}>Total Consultations</p>
          <p style={{ fontSize: 10, color: C.outline, opacity: 0.6, marginTop: 6, marginBottom: 0 }}>vs last month</p>
        </div>

        {/* Letters Generated */}
        <div className="dash-stat-card" style={{ background: '#fff', borderRadius: 16, padding: 24, border: `1px solid ${C.surfaceContainerHigh}`, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'box-shadow 0.2s' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div style={{ width: 40, height: 40, background: C.tertiaryFixed, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="description" size={20} color={C.tertiary} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#22c55e', display: 'flex', alignItems: 'center', gap: 2 }}>
              8% <Icon name="trending_up" size={14} color="#22c55e" />
            </span>
          </div>
          <p style={{ fontSize: 28, fontWeight: 900, color: C.onSurface, margin: '0 0 4px', fontFamily: 'Manrope, sans-serif' }}>{stats.letters}</p>
          <p style={{ fontSize: 12, fontWeight: 600, color: C.outline, margin: 0 }}>Letters Generated</p>
          <p style={{ fontSize: 10, color: C.outline, opacity: 0.6, marginTop: 6, marginBottom: 0 }}>vs last month</p>
        </div>

        {/* Registered Users */}
        <div className="dash-stat-card" style={{ background: '#fff', borderRadius: 16, padding: 24, border: `1px solid ${C.surfaceContainerHigh}`, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'box-shadow 0.2s' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div style={{ width: 40, height: 40, background: C.secondaryFixed, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="group" size={20} color={C.secondary} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#22c55e', display: 'flex', alignItems: 'center', gap: 2 }}>
              5% <Icon name="trending_up" size={14} color="#22c55e" />
            </span>
          </div>
          <p style={{ fontSize: 28, fontWeight: 900, color: C.onSurface, margin: '0 0 4px', fontFamily: 'Manrope, sans-serif' }}>{stats.users}</p>
          <p style={{ fontSize: 12, fontWeight: 600, color: C.outline, margin: 0 }}>Registered Users</p>
          <p style={{ fontSize: 10, color: C.outline, opacity: 0.6, marginTop: 6, marginBottom: 0 }}>new this month</p>
        </div>

        {/* Active Users */}
        <div className="dash-stat-card" style={{ background: '#fff', borderRadius: 16, padding: 24, border: `1px solid ${C.surfaceContainerHigh}`, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'box-shadow 0.2s' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div style={{ width: 40, height: 40, background: '#dcfce7', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="person_check" size={20} color="#16a34a" />
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 2 }}>
              2% <Icon name="trending_down" size={14} color="#ef4444" />
            </span>
          </div>
          <p style={{ fontSize: 28, fontWeight: 900, color: C.onSurface, margin: '0 0 4px', fontFamily: 'Manrope, sans-serif' }}>{stats.activeUsers}</p>
          <p style={{ fontSize: 12, fontWeight: 600, color: C.outline, margin: 0 }}>Active Users</p>
          <p style={{ fontSize: 10, color: C.outline, opacity: 0.6, marginTop: 6, marginBottom: 0 }}>7-day average</p>
        </div>
      </section>

      {/* ── 4. AI TOOLS + RECENT ACTIVITY ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 28, marginBottom: 32 }}>

        {/* AI Tools */}
        <div>
          <div style={{ marginBottom: 20 }}>
            <h3 className="dash-section-title">AI Tools</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14 }}>
            {tools.map((t, i) => <ToolCard key={i} {...t} />)}
          </div>
        </div>

        {/* Recent Activity */}
        <Card style={{ padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
            <h3 className="dash-section-title">Recent Activity</h3>
            <Link to="/my-appointments" style={{ fontSize: 10, fontWeight: 700, color: C.primary, textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.05em' }}>View All</Link>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <ActivityRow icon="check_circle"   iconColor="#22c55e" iconBg="#f0fdf4" title="General Medicine AI"      sub="Consultation completed"      time="Just now" />
            <ActivityRow icon="description"    iconColor="#fb923c" iconBg="#fff7ed" title="Medical Letter Generated" sub="Sick note — auto-approved"    time="12m ago" />
            <ActivityRow icon="calendar_today" iconColor="#818cf8" iconBg="#eef2ff" title="Appointment Confirmed"    sub="Patient: John D. — 10:30 am" time="1h ago" />
            <ActivityRow icon="videocam"       iconColor="#60a5fa" iconBg="#eff6ff" title="Video Call Ended"         sub="Duration: 18 min"            time="3h ago" />
          </div>
        </Card>
      </div>

      {/* ── 5. BOTTOM 3-COLUMN ROW ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24, marginBottom: 40 }}>

        {/* Quick Access */}
        <Card style={{ padding: 24 }}>
          <h3 className="dash-section-title" style={{ marginBottom: 20 }}>Quick Access</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <NavItem icon="coronavirus"   iconColor="#f87171" iconBg="#fef2f2" label="Infectious Disease AI"   sub="Specialist consultation"  path="/my-consult" />
            <NavItem icon="psychiatry"    iconColor="#4ade80" iconBg="#f0fdf4" label="General Medicine AI"     sub="GPT-4 powered"            path="/my-consult-openai" />
            <NavItem icon="edit_document" iconColor="#fb923c" iconBg="#fff7ed" label="Generate Medical Letter" sub="AI-drafted in seconds"    path="/letters" />
          </div>
        </Card>

        {/* Patient Management */}
        <Card style={{ padding: 24 }}>
          <h3 className="dash-section-title" style={{ marginBottom: 20 }}>Patient Management</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <NavItem icon="person"          iconColor={C.outline} iconBg="#f3f4f6" label="Health Profile"   sub="View / update patient info"  path="/patient-profile" />
            <NavItem icon="add_box"         iconColor="#c084fc"   iconBg="#faf5ff" label="New Appointment"  sub="Schedule a consultation"     path="/book-appointment" />
            <NavItem icon="event_available" iconColor="#2dd4bf"   iconBg="#f0fdfa" label="My Appointments"  sub="Upcoming & past"             path="/my-appointments" />
          </div>
        </Card>

        {/* Platform Overview */}
        <Card style={{ padding: 24 }}>
          <h3 className="dash-section-title" style={{ marginBottom: 20 }}>Platform Overview</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { label: 'Infectious Disease AI', badge: 'Custom AI',       bc: '#ef4444', bb: '#fef2f2', bborder: '#fecaca', path: '/my-consult' },
              { label: 'General Medicine AI',   badge: 'OpenAI GPT-4',    bc: '#22c55e', bb: '#f0fdf4', bborder: '#bbf7d0', path: '/my-consult-openai' },
              { label: 'Voice Agent',           badge: 'OpenAI Realtime', bc: '#a855f7', bb: '#faf5ff', bborder: '#e9d5ff', path: '/my-voice-agent' },
              { label: 'Video Consultation',    badge: '100ms',           bc: '#3b82f6', bb: '#eff6ff', bborder: '#bfdbfe', path: '/video-chat' },
            ].map((row, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 12px', borderRadius: 12,
                background: C.surfaceLowest, border: `1px solid ${C.surfaceContainer}`,
              }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: C.onSurface, margin: 0 }}>{row.label}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    background: row.bb, color: row.bc,
                    fontSize: 8, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.04em',
                    padding: '3px 8px', borderRadius: 4,
                    border: `1px solid ${row.bborder}`,
                  }}>{row.badge}</span>
                  <Link to={row.path} style={{
                    padding: 4, borderRadius: 6,
                    background: 'rgba(72,0,178,0.05)',
                    display: 'flex', alignItems: 'center',
                    color: C.primary, textDecoration: 'none',
                  }}>
                    <Icon name="open_in_new" size={14} color={C.primary} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ── 6. FOOTER ── */}
      <footer style={{ borderTop: `1px solid ${C.surfaceContainer}`, paddingTop: 20, textAlign: 'center' }}>
        <p style={{ fontSize: 10, color: C.outline, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>
          © 2024 InstantDrConsult. All rights reserved.
        </p>
      </footer>

      {/* ── 7. FAB ── */}
      <button className="dash-fab" onClick={() => navigate('/my-consult')} style={{
        position: 'fixed', bottom: 32, right: 32,
        width: 56, height: 56, borderRadius: '50%',
        background: C.primary, color: '#fff',
        border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 8px 24px rgba(72,0,178,0.4)',
        zIndex: 100,
      }}>
        <Icon name="add" size={26} color="#fff" className="dash-fab-icon" />
      </button>

    </div>
  )
}
